import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, FlaskConical, Save, ShieldCheck, Sparkles } from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import {
  DEFAULT_POLICY,
  linesToList,
  listToLines,
  sanitizeApi,
  type SanitizeAction,
  type SanitizeCategory,
  type SanitizePolicyInput,
  type SanitizeRuleInfo,
  type SanitizeTestResult,
} from '@/services/sanitizeApi';
import { errorStatus, problemDetail } from '@/services/tenantApi';

const CATEGORIES: { key: SanitizeCategory; label: string; description: string }[] = [
  { key: 'secret', label: 'Secrets & credentials', description: 'API keys, tokens, passwords, private keys, connection-string credentials' },
  { key: 'pii', label: 'Personal data (PII)', description: 'Email addresses and international-format phone numbers' },
  { key: 'infra', label: 'Internal infrastructure', description: 'Private IPs, internal hostnames, context-anchored AWS account IDs' },
  { key: 'term', label: 'Confidential terms', description: 'The custom terms you list below (codenames, client names, …)' },
];

const ACTIONS: { value: SanitizeAction; label: string }[] = [
  { value: 'redact', label: 'Redact' },
  { value: 'report', label: 'Report only' },
  { value: 'off', label: 'Off' },
];

const inputCls =
  'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 ' +
  'px-3 py-2 text-sm text-slate-800 dark:text-slate-200 font-mono placeholder:font-sans ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-500/40';

export default function SanitizationTab() {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [policy, setPolicy] = useState<SanitizePolicyInput>({ ...DEFAULT_POLICY });
  const [version, setVersion] = useState(0);
  const [catalog, setCatalog] = useState<SanitizeRuleInfo[]>([]);
  const [termsText, setTermsText] = useState('');
  const [domainsText, setDomainsText] = useState('');
  const [allowlistText, setAllowlistText] = useState('');

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [needsEnterprise, setNeedsEnterprise] = useState(false);

  const [testContent, setTestContent] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const [testResult, setTestResult] = useState<SanitizeTestResult | null>(null);

  useEffect(() => {
    sanitizeApi
      .getPolicy()
      .then(({ policy: loaded, catalog: rules }) => {
        const { version: v, ...input } = loaded;
        setPolicy({ ...DEFAULT_POLICY, ...input });
        setVersion(v);
        setCatalog(rules);
        setTermsText(listToLines(loaded.customTerms));
        setDomainsText(listToLines(loaded.internalDomains));
        setAllowlistText(listToLines(loaded.allowlist));
      })
      .catch((err) => {
        if (errorStatus(err) === 403) setForbidden(true);
        else setSaveError(problemDetail(err, 'Failed to load the sanitize policy'));
      })
      .finally(() => setLoading(false));
  }, []);

  const draft = useCallback(
    (): SanitizePolicyInput => ({
      ...policy,
      customTerms: linesToList(termsText),
      internalDomains: linesToList(domainsText),
      allowlist: linesToList(allowlistText),
    }),
    [policy, termsText, domainsText, allowlistText],
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaved(false);
    setNeedsEnterprise(false);
    try {
      const savedPolicy = await sanitizeApi.savePolicy(draft());
      setVersion(savedPolicy.version);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      const detail = problemDetail(err, 'Failed to save the policy');
      if (errorStatus(err) === 403 && detail.toLowerCase().includes('enterprise')) {
        setNeedsEnterprise(true);
      } else {
        setSaveError(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      setTestResult(await sanitizeApi.testPolicy(testContent, draft()));
    } catch (err) {
      setTestError(problemDetail(err, 'Dry run failed'));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <LoadingState size="lg" label="Loading sanitize policy…" />
    );
  }

  if (forbidden) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-6 text-sm text-slate-500 dark:text-slate-400">
        Sanitized Publishing policy is managed by workspace admins — the custom
        terms and internal domains it holds are themselves confidential.
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Sanitization</h1>
          {version > 0 && (
            <span className="text-xs text-slate-400 dark:text-slate-500">policy v{version}</span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Sanitized Publishing scans docs on every publish and replaces sensitive values with
          redaction chips before they reach your Knowledge Base. Originals are stored encrypted in
          the redaction vault. Redaction hides values from readers — it is not remediation:{' '}
          <span className="font-medium">rotate any real credential the scan reports.</span>
        </p>
      </div>

      {needsEnterprise && (
        <div className="rounded-xl border border-purple-200 dark:border-purple-900 bg-purple-50 dark:bg-purple-950/40 p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
          <div className="text-sm text-purple-900 dark:text-purple-200">
            <p className="font-medium">Sanitized Publishing is an Organisation-plan capability.</p>
            <p className="mt-0.5">
              You can explore the policy and dry-run tester here, but enabling enforcement needs the
              enterprise plan —{' '}
              <a href="https://www.systemdox.com/contact" target="_blank" rel="noopener noreferrer" className="underline">
                book a demo
              </a>
              .
            </p>
          </div>
        </div>
      )}

      {/* Enable + categories */}
      <section className="space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={policy.enabled}
            onChange={(e) => setPolicy((p) => ({ ...p, enabled: e.target.checked }))}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
          />
          <span>
            <span className="block text-sm font-medium text-slate-800 dark:text-slate-200">
              Scan and redact on publish
            </span>
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Applies to the docs portal publish pipeline. Findings never block a publish — content
              is redacted and every finding is reported. A scan failure fails the publish rather
              than shipping unscanned content.
            </span>
          </span>
        </label>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
          {CATEGORIES.map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between gap-4 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
              </div>
              <select
                value={policy.categories[key]}
                onChange={(e) =>
                  setPolicy((p) => ({
                    ...p,
                    categories: { ...p.categories, [key]: e.target.value as SanitizeAction },
                  }))
                }
                className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm text-slate-700 dark:text-slate-300"
              >
                {ACTIONS.map(({ value, label: actionLabel }) => (
                  <option key={value} value={value}>{actionLabel}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          “Redact” delegates to each rule's default, so false-positive-prone rules (Stripe test
          keys, AWS account IDs) stay report-only unless you override them below.
        </p>
      </section>

      {/* Terms / domains / allowlist */}
      <section className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Confidential terms <span className="font-normal text-slate-400">(one per line, max 500)</span>
          </label>
          <textarea value={termsText} onChange={(e) => setTermsText(e.target.value)} rows={4}
            placeholder={'Project Falcon\nAcme Corp migration'} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Internal domain suffixes <span className="font-normal text-slate-400">(hostnames under these are redacted)</span>
          </label>
          <textarea value={domainsText} onChange={(e) => setDomainsText(e.target.value)} rows={3}
            placeholder={'corp.acme.com\nacme.internal'} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Allowlist <span className="font-normal text-slate-400">(exact values never flagged — for intentional doc examples)</span>
          </label>
          <textarea value={allowlistText} onChange={(e) => setAllowlistText(e.target.value)} rows={3}
            placeholder="sk_test_ExampleUsedInOurDocs000000" className={inputCls} />
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            Inline escape hatches also work: <code className="font-mono">&lt;!-- sdx:allow --&gt;</code> on
            (or above) a line, <code className="font-mono">&lt;!-- sdx:allow-file --&gt;</code> in a file's
            first 10 lines. Suppressions are always counted in the scan report.
          </p>
        </div>
      </section>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60 transition-colors"
        >
          {saving ? <Spinner size="sm" tone="current" /> : <Save className="w-4 h-4" />}
          Save policy
        </button>
        {saved && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
            <Check className="w-4 h-4" /> Saved
          </span>
        )}
        {saveError && (
          <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4" /> {saveError}
          </span>
        )}
      </div>

      {/* Dry-run tester */}
      <section className="space-y-3 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Dry-run tester</h2>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Paste sample markdown to see exactly what the current draft policy would redact. Nothing
          is stored — previews use throwaway ids, not vault entries.
        </p>
        <textarea value={testContent} onChange={(e) => setTestContent(e.target.value)} rows={5}
          placeholder={'password = "hunter2"\nReach me at jane@acme.io'} className={inputCls} />
        <div className="flex items-center gap-3">
          <button
            onClick={handleTest}
            disabled={testing || !testContent.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            {testing ? <Spinner size="sm" tone="current" /> : <FlaskConical className="w-4 h-4" />}
            Test policy
          </button>
          {testError && <span className="text-sm text-red-600 dark:text-red-400">{testError}</span>}
        </div>
        {testResult && (
          <div className="space-y-3">
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300">
                {testResult.counts.redacted} redacted
              </span>
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                {testResult.counts.reported} reported
              </span>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                {testResult.counts.suppressed} suppressed
              </span>
            </div>
            {testResult.findings.length > 0 && (
              <ul className="text-xs space-y-1 text-slate-600 dark:text-slate-400">
                {testResult.findings.map((f, i) => (
                  <li key={i} className="font-mono">
                    L{f.line} · {f.ruleId} · {f.action} · “{f.preview}”
                  </li>
                ))}
              </ul>
            )}
            <pre className="rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-3 text-xs whitespace-pre-wrap text-slate-700 dark:text-slate-300">
              {testResult.redactedPreview}
            </pre>
          </div>
        )}
      </section>

      {/* Rule catalog */}
      {catalog.length > 0 && (
        <details className="rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-300">
            Detection rules ({catalog.length}) & overrides
          </summary>
          <table className="mt-3 w-full text-xs">
            <thead>
              <tr className="text-left text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                <th className="py-1.5 pr-3">Rule</th>
                <th className="py-1.5 pr-3">Severity</th>
                <th className="py-1.5 pr-3">Default</th>
                <th className="py-1.5">Override</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {catalog.map((rule) => (
                <tr key={rule.ruleId} className="text-slate-600 dark:text-slate-400">
                  <td className="py-1.5 pr-3">
                    <span className="font-mono text-slate-700 dark:text-slate-300">{rule.ruleId}</span>
                    <span className="block text-slate-400 dark:text-slate-500">{rule.description}</span>
                  </td>
                  <td className="py-1.5 pr-3">{rule.severity}</td>
                  <td className="py-1.5 pr-3">{rule.defaultAction}</td>
                  <td className="py-1.5">
                    <select
                      value={policy.ruleOverrides[rule.ruleId] || ''}
                      onChange={(e) =>
                        setPolicy((p) => {
                          const overrides = { ...p.ruleOverrides };
                          if (e.target.value) overrides[rule.ruleId] = e.target.value as SanitizeAction;
                          else delete overrides[rule.ruleId];
                          return { ...p, ruleOverrides: overrides };
                        })
                      }
                      className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-xs"
                    >
                      <option value="">—</option>
                      {ACTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
